import type { RootState } from '@src/store/store';
import type { Draft, PayloadAction } from '@reduxjs/toolkit';
import type { SupportTicket, SupportTicketStatus } from 'src/types/support-ticket';

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import api, { endpoints } from 'src/lib/axios';

// ----------------------------------------------------------------------

interface SupportTicketsState {
  data: SupportTicket[];
  total: number;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string;
}

export const initialState: SupportTicketsState = {
  data: [],
  total: 0,
  status: 'idle',
  error: '',
};

// ----------------------------------------------------------------------
// Thunks
// ----------------------------------------------------------------------

export const loadSupportTickets = createAsyncThunk<SupportTicket[], void>(
  'supportTickets/load',
  async () => {
    const response = await api.get<SupportTicket[]>(endpoints.admin.support.tickets);
    return response.data;
  }
);

export const updateSupportTicketStatus = createAsyncThunk<
  SupportTicket,
  { id: string; status: SupportTicketStatus }
>('supportTickets/updateStatus', async ({ id, status }) => {
  const response = await api.patch<SupportTicket>(
    `${endpoints.admin.support.tickets}/${id}/status`,
    { status }
  );
  return response.data;
});

export const addSupportTicketNote = createAsyncThunk<
  SupportTicket,
  { id: string; content: string }
>('supportTickets/addNote', async ({ id, content }) => {
  const response = await api.post<SupportTicket>(
    `${endpoints.admin.support.tickets}/${id}/notes`,
    { content }
  );
  return response.data;
});

/**
 * KAN-747 — answer in the user's Messagerie: the reply lands in their support
 * thread from the OnFit account, with a push. Resolves with the refreshed
 * ticket (thread and notes included).
 */
export const replyInAppToSupportTicket = createAsyncThunk<
  SupportTicket,
  { id: string; message: string }
>('supportTickets/replyInApp', async ({ id, message }) => {
  const response = await api.post<SupportTicket>(
    `${endpoints.admin.support.tickets}/${id}/reply-in-app`,
    { message }
  );
  return response.data;
});

export const replyToSupportTicket = createAsyncThunk<
  SupportTicket,
  { id: string; message: string; subject?: string }
>('supportTickets/reply', async ({ id, message, subject }) => {
  const response = await api.post<SupportTicket>(
    `${endpoints.admin.support.tickets}/${id}/reply`,
    { message, subject }
  );
  return response.data;
});

// ----------------------------------------------------------------------
// Slice
// ----------------------------------------------------------------------

const slice = createSlice({
  name: 'supportTickets',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadSupportTickets.pending, (state: Draft<SupportTicketsState>) => {
        state.status = 'loading';
      })
      .addCase(
        loadSupportTickets.fulfilled,
        (state: Draft<SupportTicketsState>, action: PayloadAction<SupportTicket[]>) => {
          state.status = 'succeeded';
          state.data = action.payload;
          state.total = action.payload.length;
          state.error = '';
        }
      )
      .addCase(loadSupportTickets.rejected, (state: Draft<SupportTicketsState>, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to load support tickets';
      })
      .addCase(
        updateSupportTicketStatus.fulfilled,
        (state: Draft<SupportTicketsState>, action: PayloadAction<SupportTicket>) => {
          const index = state.data.findIndex((ticket) => ticket.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
        }
      )
      .addCase(
        addSupportTicketNote.fulfilled,
        (state: Draft<SupportTicketsState>, action: PayloadAction<SupportTicket>) => {
          const index = state.data.findIndex((ticket) => ticket.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
        }
      )
      .addCase(
        replyInAppToSupportTicket.fulfilled,
        (state: Draft<SupportTicketsState>, action: PayloadAction<SupportTicket>) => {
          const index = state.data.findIndex((ticket) => ticket.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
        }
      )
      .addCase(
        replyToSupportTicket.fulfilled,
        (state: Draft<SupportTicketsState>, action: PayloadAction<SupportTicket>) => {
          const index = state.data.findIndex((ticket) => ticket.id === action.payload.id);
          if (index !== -1) {
            state.data[index] = action.payload;
          }
        }
      );
  },
});

export const supportTicketsReducer = slice.reducer;

// ----------------------------------------------------------------------
// Selectors
// ----------------------------------------------------------------------

export const selectSupportTickets = (state: RootState) => state.supportTickets;
